// Blog utility functions

// Format date for display
export const formatDate = (timestamp, options = {}) => {
  const date = new Date(timestamp);
  
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString('en-US', defaultOptions);
};

// Format date with time
export const formatDateTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Get relative time (e.g., "2 hours ago")
export const getRelativeTime = (timestamp) => {
  const now = Date.now();
  const diff = now - timestamp;
  
  const minute = 60 * 1000;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;
  const month = day * 30;
  const year = day * 365;
  
  if (diff < minute) {
    return 'Just now';
  } else if (diff < hour) {
    const minutes = Math.floor(diff / minute);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diff < day) {
    const hours = Math.floor(diff / hour);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diff < week) {
    const days = Math.floor(diff / day);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (diff < month) {
    const weeks = Math.floor(diff / week);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diff < year) {
    const months = Math.floor(diff / month);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diff / year);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
};

// Generate URL-friendly slug from title
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim('-');
};

// Extract plain text from HTML content
export const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Generate excerpt from content
export const generateExcerpt = (content, maxLength = 200) => {
  const textContent = stripHtml(content);
  
  if (textContent.length <= maxLength) {
    return textContent;
  }
  
  const truncated = textContent.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  return lastSpaceIndex > 0 
    ? truncated.substring(0, lastSpaceIndex) + '...'
    : truncated + '...';
};

// Calculate reading time
export const calculateReadTime = (content) => {
  const wordsPerMinute = 200;
  const words = stripHtml(content).split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
};

// Validate blog post data
export const validateBlogPost = (blogData) => {
  const errors = {};
  
  if (!blogData.title || blogData.title.trim().length === 0) {
    errors.title = 'Title is required';
  } else if (blogData.title.length > 200) {
    errors.title = 'Title must be less than 200 characters';
  }
  
  if (!blogData.content || blogData.content.trim().length === 0) {
    errors.content = 'Content is required';
  } else if (blogData.content.length > 50000) {
    errors.content = 'Content must be less than 50,000 characters';
  }
  
  if (blogData.metaDescription && blogData.metaDescription.length > 160) {
    errors.metaDescription = 'Meta description must be less than 160 characters';
  }
  
  if (blogData.tags && blogData.tags.length > 10) {
    errors.tags = 'Maximum 10 tags allowed';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Sanitize HTML content
export const sanitizeHtml = (html) => {
  // Basic HTML sanitization - in production, use a library like DOMPurify
  const allowedTags = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre'
  ];
  
  const allowedAttributes = {
    'a': ['href', 'title', 'target'],
    'img': ['src', 'alt', 'title', 'width', 'height'],
    'blockquote': ['cite']
  };
  
  // This is a basic implementation - use DOMPurify in production
  return html; // For now, return as-is
};

// Format tags for display
export const formatTags = (tags) => {
  if (!tags || !Array.isArray(tags)) {
    return [];
  }
  
  return tags.map(tag => tag.trim().toLowerCase()).filter(tag => tag.length > 0);
};

// Search blogs by query
export const searchBlogs = (blogs, query) => {
  if (!query || query.trim().length === 0) {
    return blogs;
  }
  
  const searchTerm = query.toLowerCase().trim();
  
  return blogs.filter(blog => {
    const titleMatch = blog.title.toLowerCase().includes(searchTerm);
    const contentMatch = stripHtml(blog.content).toLowerCase().includes(searchTerm);
    const tagMatch = blog.tags && blog.tags.some(tag => 
      tag.toLowerCase().includes(searchTerm)
    );
    const authorMatch = blog.author && blog.author.toLowerCase().includes(searchTerm);
    
    return titleMatch || contentMatch || tagMatch || authorMatch;
  });
};

// Sort blogs by different criteria
export const sortBlogs = (blogs, sortBy = 'createdAt', order = 'desc') => {
  const sortedBlogs = [...blogs];
  
  sortedBlogs.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'createdAt':
        aValue = a.createdAt;
        bValue = b.createdAt;
        break;
      case 'updatedAt':
        aValue = a.updatedAt;
        bValue = b.updatedAt;
        break;
      case 'publishedAt':
        aValue = a.publishedAt || 0;
        bValue = b.publishedAt || 0;
        break;
      case 'readTime':
        aValue = a.readTime || 0;
        bValue = b.readTime || 0;
        break;
      default:
        aValue = a.createdAt;
        bValue = b.createdAt;
    }
    
    if (order === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
  
  return sortedBlogs;
};

// Filter blogs by criteria
export const filterBlogs = (blogs, filters) => {
  let filteredBlogs = [...blogs];
  
  if (filters.isPublished !== undefined) {
    filteredBlogs = filteredBlogs.filter(blog => blog.isPublished === filters.isPublished);
  }
  
  if (filters.tags && filters.tags.length > 0) {
    filteredBlogs = filteredBlogs.filter(blog => 
      blog.tags && blog.tags.some(tag => filters.tags.includes(tag))
    );
  }
  
  if (filters.author) {
    filteredBlogs = filteredBlogs.filter(blog => 
      blog.author && blog.author.toLowerCase().includes(filters.author.toLowerCase())
    );
  }
  
  if (filters.dateFrom) {
    filteredBlogs = filteredBlogs.filter(blog => blog.createdAt >= filters.dateFrom);
  }
  
  if (filters.dateTo) {
    filteredBlogs = filteredBlogs.filter(blog => blog.createdAt <= filters.dateTo);
  }
  
  return filteredBlogs;
};

// Get unique tags from all blogs
export const getUniqueTags = (blogs) => {
  const allTags = blogs.reduce((tags, blog) => {
    if (blog.tags && Array.isArray(blog.tags)) {
      return [...tags, ...blog.tags];
    }
    return tags;
  }, []);
  
  return [...new Set(allTags)].sort();
};

// Paginate blogs
export const paginateBlogs = (blogs, page = 1, limit = 10) => {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  
  return {
    blogs: blogs.slice(startIndex, endIndex),
    currentPage: page,
    totalPages: Math.ceil(blogs.length / limit),
    totalBlogs: blogs.length,
    hasNextPage: endIndex < blogs.length,
    hasPrevPage: page > 1
  };
};

// Generate social media sharing URLs
export const generateShareUrls = (blog, baseUrl) => {
  const url = `${baseUrl}/blog/${blog.slug}`;
  const title = encodeURIComponent(blog.title);
  const description = encodeURIComponent(blog.excerpt || generateExcerpt(blog.content));
  
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${title}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    whatsapp: `https://wa.me/?text=${title}%20${encodeURIComponent(url)}`,
    email: `mailto:?subject=${title}&body=${description}%0A%0A${encodeURIComponent(url)}`
  };
};